/**
 * <RequireAuth /> + <RequireRole /> — 路由守卫
 *
 * 注意：前端守卫只是 UX 隐藏（按 P0-admin-auth-rbac §4）；后端 `requireRole` 中间件才是权威。
 *
 * access / refreshToken 均持久化在 localStorage（多 tab 共享）；access 缺失（旧版本只存 refresh /
 * 写入失败）时先用 refreshToken 续期（refreshAccessToken 内部 setSession）再渲染 children：
 * - ok → 渲染 children；
 * - invalid（refresh 显式失效）→ forceLogout（清会话+清 query 缓存）→ 跳登录页；续期期间会话已随他 tab
 *   登出 / 换号变了代际时不再登出（迟到的清空写盘会覆盖他 tab 的新登录），交由当前会话重新判定；
 * - transient/superseded（瞬时故障 / 在途换号）→ **保留会话**、显示重试入口，不误踢登录页。
 * 受保护子树以 sessionEpoch 为 key：他 tab 换号同步进来时整棵重挂，已打开的表单 / 列表不带着上一账号的状态
 * 以新账号身份提交。同会话续期不改 epoch，不重挂。
 * 活动会话期内 access 自然过期则由 ~/api/session-refresh 的 authedFetch 在 401 时隐式续期重试，
 * 不经本守卫——避免「常开标签页 15min 后首个请求 401 直接登出」。
 */

import type { AdminRoleLiteral } from '@novel-admin/shared';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { forceLogout, refreshAccessToken } from '~/api/session-refresh';
import { useAuthStore } from '~/stores/auth-store';

type RefreshState = 'idle' | 'refreshing' | 'failed-auth' | 'failed-transient';

const centeredStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
} as const;

export function RequireAuth() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const sessionEpoch = useAuthStore((s) => s.sessionEpoch);
  const location = useLocation();
  const [state, setState] = useState<RefreshState>('idle');

  const needsRefresh = Boolean(user && refreshToken && !accessToken);

  useEffect(() => {
    if (!needsRefresh || state !== 'idle') return;
    setState('refreshing');
    const epochAtStart = useAuthStore.getState().sessionEpoch;
    // 整页刷新后 access 丢失 → 用持久化 refresh token 续期（ok 时 setSession 已在内部完成）
    refreshAccessToken()
      .then((outcome) => {
        if (outcome.status === 'ok') {
          setState('idle');
        } else if (outcome.status === 'invalid') {
          if (useAuthStore.getState().sessionEpoch !== epochAtStart) {
            setState('idle');
            return;
          }
          forceLogout('bootstrap_refresh_invalid');
          setState('failed-auth');
        } else {
          // transient / superseded：保留会话（不清 refresh token），让用户重试而非被踢登录
          setState('failed-transient');
        }
      })
      .catch(() => setState('failed-transient'));
  }, [needsRefresh, state]);

  // 他 tab 续期成功 / 登出同步进来后不再需要续期：失败态复位，之后再缺 access 时重新自动续期
  useEffect(() => {
    if (!needsRefresh && (state === 'failed-transient' || state === 'failed-auth')) {
      setState('idle');
    }
  }, [needsRefresh, state]);

  // 瞬时故障：会话仍有效，给重试入口（再次触发续期），不清会话、不跳登录。
  // 必须先于下面 spinner 分支判别——transient 时 accessToken 仍为 null → needsRefresh 恒真，
  // 若 spinner 先命中（`refreshing || needsRefresh`）则本重试 UI 不可达、永卡「加载中…」（codex P2）。
  // 仅在仍需续期时显示：他 tab 续期成功 / 登出同步进来后 needsRefresh 归 false，直接放行或跳登录。
  if (state === 'failed-transient' && needsRefresh) {
    return (
      <div style={centeredStyle}>
        <span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>会话恢复失败（网络问题）</span>
        <button type="button" className="btn btn-accent" onClick={() => setState('idle')}>
          重试
        </button>
      </div>
    );
  }
  // spinner 仅在「正在续期」或「idle 且即将续期」时显示（failed-transient/failed-auth 已在上/下处理）
  if (state === 'refreshing' || (needsRefresh && state === 'idle')) {
    return (
      <div style={centeredStyle}>
        {/* 自绘 CSS spinner（弃 AntD Spin）：用 var(--token) 上色，旋转动画内联注入 */}
        <style>{'@keyframes na-spin{to{transform:rotate(360deg)}}'}</style>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            animation: 'na-spin 0.8s linear infinite',
          }}
        />
        <span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>加载中…</span>
      </div>
    );
  }
  if (user && accessToken) {
    return <Outlet key={sessionEpoch} />;
  }
  return <Navigate to={`/auth/login?from=${encodeURIComponent(location.pathname)}`} replace />;
}

export function RequireRole({ roles }: { roles: AdminRoleLiteral[] }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}
